// src/components/dialog/DlgSensorAdd.tsx

import { CheckTwoTone, MemoryTwoTone } from '@mui/icons-material'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { SensorPool } from '@/types/domain/sensor'
import { KARMA_BasicDialog } from '@/types/karma/dialog'

import { KSelect } from '..'
import { useSensorPool } from '../../hooks'

type Props = KARMA_BasicDialog & {
  onChange: (_: any) => void
  folderSensors: any[]
}

export const DlgSensorAdd = ({
  onChange,
  onClose,
  open,
  folderSensors,
}: Props) => {
  const theme = useTheme()
  const { white: color } = theme.palette

  const sPool: SensorPool = useSensorPool()

  const [deviceIdList, setDeviceIdList] = useState([])
  const [providerList, setProviderList] = useState([])
  const [sensorTypeList, setSensorTypeList] = useState([])
  const [availableSensors, setAvailableSensors] = useState([])

  const [providerFilter, setProviderFilter] = useState('')
  const [sensorTypeFilter, setSensorTypeFilter] = useState('')
  const [deviceIdFilter, setDeviceIdFilter] = useState('')

  useEffect(() => {
    if (sPool.length === 0) return

    const PROVIDER = f => Boolean(f.attributes.provider === providerFilter)

    const DEVICE_ID = f =>
      Boolean(
        deviceIdFilter !== '' ? f.attributes.device_id === deviceIdFilter : f
      )

    const SENSORTYPE = f =>
      Boolean(
        sensorTypeFilter !== ''
          ? f.attributes.sensor_type === sensorTypeFilter
          : f
      )

    setDeviceIdList(
      [
        ...new Set(
          sPool
            .filter(PROVIDER)
            .filter(SENSORTYPE)
            .map(item => item.attributes.device_id)
        ),
      ].sort()
    )

    setProviderList(
      [...new Set(sPool.map(item => item.attributes.provider))].sort()
    )

    setSensorTypeList(
      [
        ...new Set(
          sPool
            .filter(PROVIDER)
            .filter(DEVICE_ID)
            .map(item => item.attributes.sensor_type)
        ),
      ].sort()
    )

    setAvailableSensors(
      sPool.filter(PROVIDER).filter(SENSORTYPE).filter(DEVICE_ID)
    )
  }, [sPool, deviceIdFilter, providerFilter, sensorTypeFilter])

  const onChangeDeviceId = event => setDeviceIdFilter(event.target.value)
  const onChangeProvider = event => setProviderFilter(event.target.value)
  const onChangeSensorType = event => setSensorTypeFilter(event.target.value)

  const disabled = providerFilter === ''

  return (
    <Dialog fullWidth={true} onClose={onClose} open={open}>
      <DialogTitle>Sensor hinzufügen</DialogTitle>

      <DialogContent>
        <KSelect
          icon={<MemoryTwoTone />}
          label={'Provider'}
          onChange={onChangeProvider}
          value={providerFilter}
        >
          <MenuItem value={''}>Provider wählen ...</MenuItem>

          {providerList.map(item => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </KSelect>

        <KSelect
          disabled={disabled}
          icon={<MemoryTwoTone />}
          label={'Device ID'}
          onChange={onChangeDeviceId}
          value={deviceIdFilter}
        >
          <MenuItem value={''}>Device ID wählen ...</MenuItem>

          {deviceIdList.map(item => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </KSelect>

        <KSelect
          disabled={disabled}
          icon={<MemoryTwoTone />}
          label={'Sensor Typ'}
          onChange={onChangeSensorType}
          value={sensorTypeFilter}
        >
          <MenuItem value={''}>Sensor Typ wählen ...</MenuItem>

          {sensorTypeList.map(item => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </KSelect>

        <KSelect
          disabled={disabled}
          icon={<MemoryTwoTone />}
          label={'Sensor'}
          onChange={onChange}
          value={folderSensors}
        >
          <MenuItem disabled>Sensor wählen ...</MenuItem>

          {availableSensors.map(s => {
            const { device_id, is_active, name, provider, sensor_type } =
              s.attributes

            return (
              folderSensors
                .map(item => Number(item.id))
                .filter(f => f !== Number(s.id)) && (
                <MenuItem key={s} value={s} disabled={is_active === 0}>
                  [{provider} :: {sensor_type} :: {device_id} :: {name}]
                </MenuItem>
              )
            )
          })}
        </KSelect>
      </DialogContent>

      <DialogActions>
        <IconButton onClick={onClose} sx={{ color }}>
          <CheckTwoTone />
        </IconButton>
      </DialogActions>
    </Dialog>
  )
}
